import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Modal from './ModalTrue';
import './LenderSuggestions.css';

interface Suggestion {
  id: string;
  originalName: string;
  suggestedCanonical: string;
  confidence: number;
  reasoning?: string;
  status: string;
  firstSeen: string;
  lastSeen: string;
}

interface CanonicalOption {
  name: string;
  email?: string;
}

const LenderSuggestions: React.FC = () => {
  const { apiRequest } = useAuth();
  const [data, setData] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [canonicals, setCanonicals] = useState<CanonicalOption[]>([]);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [selectedCanonical, setSelectedCanonical] = useState<string>('');
  const [assignError, setAssignError] = useState<string>('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [reasoningModal, setReasoningModal] = useState<Suggestion | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await apiRequest({ method: 'GET', url: '/ui/admin/lender-suggestions' }) as Suggestion[];
      setData(resp);
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to fetch suggestions');
    } finally {
      setLoading(false);
    }
  };

  const fetchCanonicals = async () => {
    try {
      const resp = await apiRequest({ method: 'GET', url: '/ui/admin/lenders/all' }) as { lenders: CanonicalOption[] };
      setCanonicals(resp?.lenders ?? []);
    } catch (err) {
      console.error('Failed to load canonical lenders', err);
    }
  };

  useEffect(() => {
    fetchData();
    fetchCanonicals();
  }, []);

  const handleAction = async (id: string, action: 'accept' | 'reject') => {
    try {
      await apiRequest({ method: 'POST', url: `/ui/admin/lender-suggestions/${encodeURIComponent(id)}/${action}` });
      await fetchData();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.error || `Failed to ${action} suggestion`);
    }
  };

  const handleAssignStart = (suggestion: Suggestion) => {
    setAssigningId(suggestion.id);
    setAssignError('');
    const defaultCanonical = canonicals.find(c => c.name.toLowerCase() === suggestion.suggestedCanonical.toLowerCase());
    setSelectedCanonical(defaultCanonical ? defaultCanonical.name : '');
  };

  const handleAssignCancel = () => {
    setAssigningId(null);
    setSelectedCanonical('');
    setAssignError('');
  };

  const handleAssignSubmit = async () => {
    if (!assigningId) return;
    if (!selectedCanonical) {
      setAssignError('Please select an existing lender.');
      return;
    }

    setAssignLoading(true);
    setAssignError('');
    try {
      await apiRequest({
        method: 'POST',
        url: `/ui/admin/lender-suggestions/${encodeURIComponent(assigningId)}/assign`,
        data: { canonicalName: selectedCanonical },
      });
      setAssigningId(null);
      setSelectedCanonical('');
      await fetchData();
    } catch (err: any) {
      console.error(err);
      setAssignError(err.response?.data?.error || 'Failed to assign suggestion');
    } finally {
      setAssignLoading(false);
    }
  };

  const canonicalOptions = useMemo(() => canonicals.map(c => ({ value: c.name, label: c.email ? `${c.name} (${c.email})` : c.name })), [canonicals]);

  if (loading) return <div>Loading suggestions…</div>;
  if (error) return <div className="error">{error}</div>;
  if (data.length === 0) return <p>No pending suggestions 🎉</p>;

  return (
    <div className="lender-suggestions">
      <table className="suggestion-table">
        <thead>
          <tr>
            <th>Original</th>
            <th>Suggestion</th>
            <th>Confidence</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.map(s => (
            <React.Fragment key={s.id}>
              <tr>
                <td>{s.originalName}</td>
                <td>{s.suggestedCanonical || '—'}</td>
                <td>{s.confidence != null && !isNaN(s.confidence) ? `${(s.confidence * 100).toFixed(0)}%` : '—'}</td>
                <td className="suggestion-actions">
                  <button
                    className="accept-btn"
                    onClick={() => handleAction(s.id, 'accept')}
                    disabled={assignLoading && assigningId === s.id}
                  >
                    Accept
                  </button>
                  <button
                    className="reject-btn"
                    onClick={() => handleAction(s.id, 'reject')}
                    disabled={assignLoading && assigningId === s.id}
                  >
                    Reject
                  </button>
                  <button
                    className="assign-btn"
                    onClick={() => handleAssignStart(s)}
                    disabled={assignLoading && assigningId === s.id}
                  >
                    Assign
                  </button>
                  <button
                    className="reasoning-btn"
                    onClick={() => s.reasoning && setReasoningModal(s)}
                    disabled={!s.reasoning}
                    title={s.reasoning ? 'View reasoning' : 'No reasoning available'}
                  >
                    Reasoning
                  </button>
                </td>
              </tr>
              {assigningId === s.id && (
                <tr className="suggestion-assign-row">
                  <td colSpan={4}>
                    <div className="assign-controls">
                      <label htmlFor="assignCanonical" className="assign-label">Assign to existing lender:</label>
                      <select
                        id="assignCanonical"
                        className="assign-select"
                        value={selectedCanonical}
                        onChange={e => setSelectedCanonical(e.target.value)}
                        disabled={assignLoading}
                      >
                        <option value="">Select lender…</option>
                        {canonicalOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <button
                        className="assign-confirm"
                        onClick={handleAssignSubmit}
                        disabled={assignLoading}
                      >
                        {assignLoading ? 'Assigning…' : 'Assign'}
                      </button>
                      <button className="assign-cancel" onClick={handleAssignCancel} disabled={assignLoading}>Cancel</button>
                    </div>
                    {assignError && <div className="assign-error">{assignError}</div>}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {reasoningModal && (
        <Modal onClose={() => setReasoningModal(null)}>
          <h3>Lender Suggestion Reasoning</h3>
          <p><strong>Original:</strong> {reasoningModal.originalName}</p>
          <p><strong>Suggested:</strong> {reasoningModal.suggestedCanonical || '—'}</p>
          <p><strong>Confidence:</strong> {reasoningModal.confidence != null && !isNaN(reasoningModal.confidence) ? `${(reasoningModal.confidence * 100).toFixed(1)}%` : '—'}</p>
          <div className="reasoning-text">
            {reasoningModal.reasoning ? (
              <p>{reasoningModal.reasoning}</p>
            ) : (
              <p>No reasoning was provided.</p>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default LenderSuggestions; 
